import { Injectable } from '@angular/core';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

/**
 * Central coordinator for background data refresh in the admin org/subscription views.
 *
 * Merges three signal sources:
 *  1. Manual trigger  — called after mutations or the Refresh button
 *  2. Tab focus       — visibilitychange → visible (debounced 500 ms)
 *  3. Background poll — every 60 s while the tab is visible
 *
 * Components subscribe to refresh$ and perform a silent background refetch.
 * They should guard against concurrent loads and preserve existing data on
 * failure so the user never sees a blank view due to a background error.
 */
@Injectable({ providedIn: 'root' })
export class OrgRefreshService {
  private readonly manual$ = new Subject<void>();

  readonly refresh$ = merge(
    this.manual$,
    fromEvent<Event>(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      debounceTime(500),
    ),
    timer(60_000, 60_000).pipe(
      filter(() => document.visibilityState === 'visible'),
    ),
  );

  trigger(): void {
    this.manual$.next();
  }
}
